import type { OrderLeg } from '../services/orders.service';
import type { LegWireField } from './legSavePayload';

// Returns all legrecnos in the same dispatch scope as the given legrecno within a legs array
export const getDispatchScope = (legrecno: number, legs: OrderLeg[]): number[] => {
  const legIndex = legs.findIndex(l => l.legrecno === legrecno);
  if (legIndex === -1) return [legrecno];
  let scopeStart = 0;
  for (let i = 0; i < legIndex; i++) {
    if (legs[i].legDrop === 'Y') scopeStart = i + 1;
  }
  const scope: number[] = [];
  for (let i = scopeStart; i < legs.length; i++) {
    scope.push(legs[i].legrecno);
    if (legs[i].legDrop === 'Y') break;
  }
  return scope;
};

// Legs that split out of the dispatch grouping when `legrecno` becomes drop=Y:
// the contiguous run from the next leg through the next pre-existing drop
// (inclusive of that terminating drop). These are the legs that were previously
// grouped with `legrecno` and now form their own scope. Mirrors the `affected`
// slice in cascadeLegDropChange (orderLegInheritance.ts).
export const getLegsLeavingScopeOnDrop = (legrecno: number, legs: OrderLeg[]): OrderLeg[] => {
  const legIndex = legs.findIndex(l => l.legrecno === legrecno);
  if (legIndex === -1 || legIndex >= legs.length - 1) return [];
  const affected: OrderLeg[] = [];
  for (let i = legIndex + 1; i < legs.length; i++) {
    affected.push(legs[i]);
    if (legs[i].legDrop === 'Y') break;
  }
  return affected;
};

// --- pre-save stale check: which leg fields to compare ---------------------------
//
// This list used to be 21 hand-written `if` comparisons, and what it left out is how a
// stale browser copy of a leg reached the save untouched: a driver's document resolved
// destrequireinterchange 'Y' -> 'RESOLVED' while the pro sat open, the pre-save check
// never looked at that column, and the saverules engine then recomputed the settled flag
// to 'N'. Measured over 44,341 captured saves: 466 had real leg drift and the old list
// would have surfaced 10 of them (2.1%).
//
// So the list is now DERIVED from LEG_WIRE_FIELDS — every field that ships in a save
// payload must be classified as compared or deliberately ignored, and the type assertions
// below fail the build if one is missed. Adding a payload field can no longer silently
// fall out of conflict detection.
//
// COMPARED: anything a user can change from /pro (the legs grid, the Dispatch Details
// modal's Y/N/Resolved dropdowns, the document recnos those dropdowns move with) plus the
// four arrival/depart datetimes, which some users can edit directly.
const CONFLICT_CHECKED_WIRE_FIELDS = [
  'legtype', 'legAction2', 'legDrop', 'driver', 'donotpayleg', 'dispatchid',
  'origcode', 'origApptDate', 'origApptTime1', 'origApptTime2',
  'origApptNumber', 'origmemo',
  'destcode', 'destApptDate', 'destApptTime1', 'destApptTime2',
  'destApptNumber', 'legmemo',
  'origArrivalDateTime', 'origDepartDateTime', 'destArrivalDateTime', 'destDepartDateTime',
  'origrequireinspection', 'origrequireinterchange', 'origrequirebol',
  'destrequirepod', 'destrequireinterchange', 'destrequirescaleticket',
  'originspectionrecno', 'originterchangerecno', 'origbolrecno',
  'destpodrecno', 'destinterchangerecno', 'destscaleticketrecno',
] as const;

// IGNORED, and why — surfacing these would abort a save over a field the user cannot act
// on, so they would simply hit save again:
//   * legnum / legdeleted — structural. The app renumbers legs itself, and adds/removes
//     are already reported as __new__ / __removed__ below.
//   * the four *geo* datetimes, legstatus, dspdriverdatetime — driver-app and dispatch
//     telemetry, not editable in /pro. These are the largest real-drift category (339 of
//     the 466 drifted saves are telemetry-only); the backend reads the DB value for them
//     instead of the stale echo, so nothing is lost by staying quiet.
//   * orig/destapptrequesteddatetime — written by the appointment-request flow, not the
//     leg editor. Note these two are NOT tracked in the backend's leg snapshot, so unlike
//     the rest they carry no drift measurement either way.
//   * origName / destName — the location PICKER LABEL ("GWI31 - GWI31 ( BROADWAY YARD )"),
//     not a column. The backend reads the raw joined locations.lname
//     ("GWI31 ( BROADWAY YARD )"), so comparing them there flagged 95% of all saves
//     (measured on 19,212 captured production saves). Here the comparison is fresh-vs-
//     loaded, both in this same format, so it never misfired — but the label is derived
//     from orig/destcode, which IS compared, so checking it adds nothing and it would
//     misfire if a location were ever renamed underneath an open pro.
const CONFLICT_EXEMPT_WIRE_FIELDS = [
  'legnum', 'legdeleted',
  'origgeoarrivaldatetime', 'origgeodepartdatetime',
  'destgeoarrivaldatetime', 'destgeodepartdatetime',
  'origapptrequesteddatetime', 'destapptrequesteddatetime',
  'legstatus', 'dspdriverdatetime',
  'origName', 'destName',
] as const;

// Compared but NOT on the wire: derived display strings that move with orig/destcode, and
// two flags owned by other subsystems that still matter to someone editing the leg
// (`isPaid` = driver pay approved underneath you; `sendToGwiMove` rides outside `new`).
const CONFLICT_CHECKED_EXTRA_FIELDS = [
  'origCityState', 'destCityState', 'loadedLeg', 'isPaid', 'sendToGwiMove',
] as const;

// Build-time guard: CONFLICT_CHECKED_WIRE_FIELDS and CONFLICT_EXEMPT_WIRE_FIELDS must
// partition LEG_WIRE_FIELDS exactly — no unclassified field, no stray name.
type ClassifiedWireField =
  | typeof CONFLICT_CHECKED_WIRE_FIELDS[number]
  | typeof CONFLICT_EXEMPT_WIRE_FIELDS[number];
const _assertEveryWireFieldClassified: Exclude<LegWireField, ClassifiedWireField> extends never
  ? true : never = true;
const _assertNoStrayClassification: Exclude<ClassifiedWireField, LegWireField> extends never
  ? true : never = true;
void _assertEveryWireFieldClassified;
void _assertNoStrayClassification;

export const CONFLICT_CHECKED_LEG_FIELDS: ReadonlyArray<keyof OrderLeg> = [
  ...CONFLICT_CHECKED_WIRE_FIELDS,
  ...CONFLICT_CHECKED_EXTRA_FIELDS,
] as ReadonlyArray<keyof OrderLeg>;

// v1 cmsapi.pl normalizes blank -> 0 on these before comparing, so 0 and '' are the same
// value (mirrors payload_leg_field_touched on the backend).
const NUMERIC_LEG_FIELD = /(code|recno|dispatchid)$/;
// Columns where legsall stores '' for the same "no" the UI spells 'N' (mirrors the
// backend's EMPTY_MEANS_N_FIELDS) — a blank must not read as a change to 'N'.
const EMPTY_MEANS_N: ReadonlySet<string> = new Set([
  'donotpayleg', 'legDrop',
  'origrequireinspection', 'origrequireinterchange', 'origrequirebol',
  'destrequirepod', 'destrequireinterchange', 'destrequirescaleticket',
]);

const normalizeLegValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Y' : '';
  return String(value).trim();
};

/** Do these two values of `field` mean the same thing? Both sides come from the same
 *  order-details endpoint, so this only has to absorb blank/0/false spellings. */
export const legValuesEqual = (field: string, a: unknown, b: unknown): boolean => {
  let x = normalizeLegValue(a);
  let y = normalizeLegValue(b);
  if (NUMERIC_LEG_FIELD.test(field)) {
    if (x === '') x = '0';
    if (y === '') y = '0';
  } else if (EMPTY_MEANS_N.has(field)) {
    if (x === '') x = 'N';
    if (y === '') y = 'N';
  }
  return x === y;
};

// Human labels for the conflict callout. Keyed by the same field names the diff above
// reports, plus the two structural markers.
const LEG_FIELD_LABELS: Record<string, string> = {
  __new__: 'leg added',
  __removed__: 'leg deleted',
  legtype: 'Origin Move Type',
  legAction2: 'Destination Move Type',
  legDrop: 'Drop',
  driver: 'Driver',
  donotpayleg: 'Do Not Pay',
  dispatchid: 'Dispatch',
  origcode: 'Origin',
  origName: 'Origin',
  origCityState: 'Origin City/State',
  origApptDate: 'Pickup Appt Date',
  origApptTime1: 'Pickup Appt Time',
  origApptTime2: 'Pickup Appt Time',
  origApptNumber: 'Pickup Number',
  origmemo: 'Origin Memo',
  destcode: 'Destination',
  destName: 'Destination',
  destCityState: 'Destination City/State',
  destApptDate: 'Delivery Appt Date',
  destApptTime1: 'Delivery Appt Time',
  destApptTime2: 'Delivery Appt Time',
  destApptNumber: 'Delivery Number',
  legmemo: 'Leg Memo',
  origArrivalDateTime: 'Origin Arrival',
  origDepartDateTime: 'Origin Depart',
  destArrivalDateTime: 'Destination Arrival',
  destDepartDateTime: 'Destination Depart',
  origrequireinspection: 'Inspection',
  origrequireinterchange: 'Out Interchange',
  origrequirebol: 'BOL',
  destrequirepod: 'POD',
  destrequireinterchange: 'In Interchange',
  destrequirescaleticket: 'Scale Ticket',
  originspectionrecno: 'Inspection document',
  originterchangerecno: 'Out Interchange document',
  origbolrecno: 'BOL document',
  destpodrecno: 'POD document',
  destinterchangerecno: 'In Interchange document',
  destscaleticketrecno: 'Scale Ticket document',
  loadedLeg: 'Loaded',
  isPaid: 'Driver Pay',
  sendToGwiMove: 'Send to gwiMove',
};

/** The conflicting field names recorded for one leg, from the ConflictMap's
 *  `leg:{legrecno}:{field}` keys (see useOrderEditing). */
export const legConflictFields = (
  conflicts: Record<string, unknown> | undefined,
  legrecno: number,
): string[] => {
  if (!conflicts) return [];
  const prefix = `leg:${legrecno}:`;
  return Object.keys(conflicts)
    .filter((k) => k.startsWith(prefix))
    .map((k) => k.slice(prefix.length));
};

/** Human-readable, de-duplicated list of what changed — several field names share a
 *  label (origcode/origName are both just "Origin"), so collapse them. */
export const describeLegConflictFields = (fields: string[]): string =>
  [...new Set(fields.map((f) => LEG_FIELD_LABELS[f] ?? f))].join(', ');

// Helper to detect which leg fields changed between two leg lists (including new legs)
export const detectChangedFields = (oldLegs: OrderLeg[], newLegs: OrderLeg[]): Record<number, string[]> => {
  const changed: Record<number, string[]> = {};
  oldLegs.forEach(oldLeg => {
    const newLeg = newLegs.find(l => l.legrecno === oldLeg.legrecno);
    if (!newLeg) return;
    const fields = CONFLICT_CHECKED_LEG_FIELDS.filter(
      f => !legValuesEqual(f as string, oldLeg[f], newLeg[f])
    ) as string[];
    if (fields.length > 0) changed[oldLeg.legrecno] = fields;
  });
  // Detect legs added in V1 that weren't in the original data
  newLegs.forEach(newLeg => {
    if (!oldLegs.find(l => l.legrecno === newLeg.legrecno)) {
      changed[newLeg.legrecno] = ['__new__'];
    }
  });
  // Detect legs removed in V1 that were in the original data
  oldLegs.forEach(oldLeg => {
    if (!newLegs.find(l => l.legrecno === oldLeg.legrecno)) {
      changed[oldLeg.legrecno] = ['__removed__'];
    }
  });
  return changed;
};
